import {
	ImagePositionEnum,
	QuotationSizeEnum,
	type SlideContent,
	SlideLayoutEnum
} from '@soomo/lib/types/index';

export function getDefaultLayoutProps(
	layout: SlideLayoutEnum,
	prevContent: SlideContent
): SlideContent {
	let defaults: SlideContent;

	switch (layout) {
		case SlideLayoutEnum.Text:
			defaults = { layout, text: '' };
			break;
		case SlideLayoutEnum.TextImage:
			defaults = {
				layout,
				text: '',
				url: '',
				alt: '',
				credit: '',
				description: '',
				position: ImagePositionEnum.Top
			};
			break;
		case SlideLayoutEnum.TitleImage:
			defaults = { layout, title: '', url: '', alt: '', credit: '', description: '' };
			break;
		case SlideLayoutEnum.TitleSubtitle:
			defaults = { layout, title: '', subtitle: '' };
			break;
		case SlideLayoutEnum.Quotation:
			defaults = { layout, text: '', attribution: '', size: QuotationSizeEnum.Small };
			break;
		default:
			break;
	}

	// Preserve previous content for matching keys
	const result: Partial<SlideContent> = { layout };
	for (const key in defaults) {
		if (key === 'layout') continue;
		result[key] = prevContent[key] ?? defaults[key];
	}

	return result as SlideContent;
}

export function isWithTitle(content: SlideContent): content is SlideContent & { title: string } {
	return 'title' in content;
}

export function isWithText(content: SlideContent): content is SlideContent & { text: string } {
	return 'text' in content;
}

export function isWithImage(
	content: SlideContent
): content is SlideContent & { url: string; alt: string; credit: string; description: string } {
	return 'url' in content && 'alt' in content && 'credit' in content;
}

export function getSlidePreviewText(content: SlideContent, maxLength = 50): string {
	const text = isWithText(content) ? content.text : content.title;
	return new DOMParser().parseFromString(text, 'text/html').body.textContent.slice(0, maxLength);
}

export function showLayoutSwitchWarning(
	prevContent: SlideContent,
	newContent: SlideContent
): boolean {
	if (newContent.layout === prevContent.layout) return false;

	// Warn if any property in prevContent is not present in newContent and is non-empty
	return Object.keys(prevContent).some(
		(key) =>
			key !== 'layout' &&
			!(key in newContent) &&
			prevContent[key] !== undefined &&
			prevContent[key] !== null &&
			prevContent[key] !== ''
	);
}
